import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, ChevronRight } from 'lucide-react';
import { useEmpresas } from '@/hooks/useEmpresa';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import type { EmpresaListItem } from '@/types/empresa';

export function EmpresasList() {
  const { user } = useAuth();
  const { data, isLoading, error } = useEmpresas();
  const [somenteSemConsultor, setSomenteSemConsultor] = useState(false);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="h-10 w-10 rounded-full border-2 border-ds-border border-t-ds-success animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-ds-danger/30 bg-ds-danger/10 p-6 text-ds-danger">
        <p className="font-medium">Erro ao carregar empresas</p>
        <p className="text-sm mt-1">Tente novamente mais tarde.</p>
      </div>
    );
  }

  const empresas = data ?? [];
  const isAdmin = user?.role === 'admin';
  const empresasExibidas = isAdmin && somenteSemConsultor
    ? empresas.filter((empresa) => !empresa.consultor_id)
    : empresas;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-ds-text-primary">Empresas</h1>
          <p className="text-ds-text-secondary mt-1">
            Perfis técnicos das empresas cadastradas.
          </p>
        </div>
        {isAdmin && (
          <label className="flex items-center gap-2 text-sm text-ds-text-secondary shrink-0 mt-1">
            <input
              type="checkbox"
              checked={somenteSemConsultor}
              onChange={(e) => setSomenteSemConsultor(e.target.checked)}
              className="rounded border-ds-border text-ds-success focus:ring-ds-success/50"
            />
            Somente sem consultor
          </label>
        )}
      </div>

      {empresasExibidas.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <Building2 className="h-10 w-10 text-ds-text-secondary mx-auto mb-3" />
            <p className="text-ds-text-secondary">
              {somenteSemConsultor ? 'Todas as empresas têm consultor atribuído.' : 'Nenhuma empresa cadastrada ainda.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {empresasExibidas.map((empresa: EmpresaListItem) => (
            <Link
              key={empresa.id}
              to={`/empresas/${empresa.id}/perfil-tecnico`}
              className="group"
            >
              <Card className="transition-all duration-200 hover:border-ds-success/50 hover:shadow-sm">
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="h-10 w-10 rounded-lg bg-ds-success/10 flex items-center justify-center">
                      <Building2 className="h-5 w-5 text-ds-success" />
                    </div>
                    <div>
                      <CardTitle className="text-base text-ds-text-primary group-hover:text-ds-success transition-colors">
                        {empresa.razao_social}
                      </CardTitle>
                      <p className="text-sm text-ds-text-secondary">CNPJ: {empresa.cnpj}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {isAdmin && !empresa.consultor_id && (
                      <Badge variant="warning" size="sm">Sem consultor</Badge>
                    )}
                    <ChevronRight className="h-5 w-5 text-ds-text-secondary group-hover:text-ds-success transition-colors" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
